
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import Dialog from "@/app/ui/components/dialog/index.vue";
import Search from "@/app/ui/components/search/index.vue";
import SelectSearch from "@/app/ui/components/select-search/index.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ManageAreaController } from "@/app/ui/controllers/ManageAreaController";
import TableTransferArea from "@/app/ui/views/pickup-dashboard/manage-area/components/table-transfer-area/index.vue";
import {
  ListManageAreaApiRequest,
  TransferAreaApiRequest,
} from "@/data/payload/api/ManageAreaApiRequest";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { DetailPartnerAreaDedicated, ManageArea } from "@/domain/entities/ManageArea";
import { Pagination } from "@/domain/entities/Pagination";
import { debounce } from "lodash";
import { computed, defineComponent, ref, watch } from "vue";
import { useRouter } from "vue-router";
import i18n from "@/i18n";

export default defineComponent({
  name: "TransferAreaModal",
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    data: {
      type: DetailPartnerAreaDedicated,
      required: true,
    }
  },
  components: {
    Dialog,
    Search,
    SelectSearch,
    TableTransferArea,
  },
  emits: ["update:modelValue", "submit-success"],

  setup(props, { emit }) {
    const t = (key: string, params?: Record<string, unknown>) =>
      String(i18n.global.t(key, params));
    const isLoadingFilter = ref<boolean>(false);
    const isLoadingData = ref<boolean>(false);
    const filter = ref(new ListManageAreaApiRequest());
    const pagination = ref<Pagination>(new Pagination(1, 10));
    const destinationValue = ref<ManageArea>();
    const selectSearchDataDestination = ref<ManageArea[]>([]);
    const statusSelectSearchDestination = ref<boolean>(false);
    const selectSearchDestinationNotFound = ref<boolean>(false);
    const originalTransferAreaData = ref<any>([]);
    const selectedIds = ref<string[]>([]);

    const router = useRouter()

    const fetchPartnerAreaList = async () => {
      try {
        isLoadingFilter.value = true;
        const respons = await ManageAreaController.getList(filter.value);
        selectSearchDataDestination.value = [...respons.list].filter(d => d.uid != props.data.uid);
      } catch (error) {
        console.error("error fetch partner area: ", error)
      } finally {
        isLoadingFilter.value = false;
      }
    };

    const onSelectSearchDestination = debounce(async (value: string) => {
      filter.value.q = value;
      await fetchPartnerAreaList();
    }, 300);

    const onCloseSelectSearchDestination = () => {
      filter.value.q = "";
      statusSelectSearchDestination.value = false;
    };

    const onChooseSelectSearchDestination = (item: ManageArea) => {
      destinationValue.value = item;
      statusSelectSearchDestination.value = true;
    };

    const search = ref<string>("");
    const isShowModal = computed({
      get: () => props.modelValue,
      set: (value: boolean) => {
        emit("update:modelValue", value);
      },
    });

    const onSearch = (value: string) => {
      search.value = value;
      pagination.value.page = 1;
    };

    const showActionToast = () => {
      const message = `
        <div style="color: #FFFFFF; font-size: 12px;">
         <b> ${selectedIds.value.length}</b> Kelurahan berhasil ditransfer ke <b>${destinationValue.value?.areaName}.</b>
        </div>
      `;

      MainAppController.setSnackbarMessage(message);
      MainAppController.setSnackbarPositivity(true);
      MainAppController.setSnackbarTimeout(5000);
      MainAppController.setSnackbarBackgroundColor("#00875A");
      MainAppController.setSnackbarBackgroundColor("#00875A");
      MainAppController.setSnackbarIconLeft("icon-check-white");
      MainAppController.setSnackbar(true);
    };

    const sendTransferArea = async () => {
      MainAppController.closeMessageModal();
      MainAppController.showLoading();

      const payload = new TransferAreaApiRequest({
        originAreaUid: props.data.uid,
        destinationAreaUid: destinationValue.value?.uid,
        geoAddressId: selectedIds.value,
      });

      try {
        await ManageAreaController.postTransferArea(payload);

        MainAppController.closeMessageModal();
        emit("update:modelValue", false);

        router.push("/pickup-dashboard/manage-area")
        showActionToast();
        emit("submit-success")
      } catch (error) {
        const errorMessageId =
          (error as any)?.response?.data?.message?.id || "";
        const errorMessage = t(
          "pickupDashboard.manageArea.transferFailedMessage"
        );
        const msg = errorMessageId || errorMessage;

        MainAppController.showMessageModal(
          new ModalMessageEntities({
            image: "new-badge-failed",
            title: t("pickupDashboard.manageArea.transferFailedTitle"),
            message: msg,
            textSuccess: t("pickupDashboard.tryAgain"),
            textCancel: t("pickupDashboard.manageArea.back"),
            onClose: () => {
              MainAppController.closeMessageModal();
            },
            onSubmit: async () => {
              MainAppController.closeMessageModal();
              await sendTransferArea();
            },
          }),
        );
      } finally {
        MainAppController.closeLoading();
      }
    };

    const handleConfirmation = () => {
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          image: "badge-confirmation-general",
          title: t("pickupDashboard.manageArea.transferConfirmTitle", {
            count: selectedIds.value.length,
            destinationAreaName: destinationValue.value?.areaName
          }),
          message: t("pickupDashboard.manageArea.transferConfirmMessage"),
          onSubmit: async () => await sendTransferArea(),
          onClose: () => {
            MainAppController.closeMessageModal();
          },
          textSuccess: t("pickupDashboard.manageArea.confirmTransfer"),
          textCancel: t("pickupDashboard.manageArea.cancel"),
          customWidth: "417px",
          customButton: "w-full",
          customParentButton: "w-full",
          customWrapperButton: "w-full px-6",
        }),
      );
    };

    const sortKey = ref<string | null>(null);
    const sortOrder = ref<"asc" | "desc" | null>(null);

    const columns = ref<
      Array<{
        key: string;
        label: string;
        sortable?: boolean;
        style?: { width: string; textAlign?: string };
      }>
    >([
      {
        key: "no",
        label: "pickupDashboard.manageArea.transferColumn.no",
        style: { width: "60px", textAlign: "center" }
      },
      {
        key: "villageName",
        label: "pickupDashboard.manageArea.transferColumn.village",
        sortable: true
      },
      {
        key: "subDistrictName",
        label: "pickupDashboard.manageArea.transferColumn.subDistrict",
        sortable: true
      },
      {
        key: "districtName",
        label: "pickupDashboard.manageArea.transferColumn.district",
        sortable: true
      },
      {
        key: "provinceName",
        label: "pickupDashboard.manageArea.transferColumn.province",
        sortable: true
      }
    ]);

    const startIndex = computed(
      () => (pagination.value.page - 1) * pagination.value.limit,
    );

    const errorCause = ref<string>("");
    const setErrorCause = (err: any) => {
      errorCause.value = err.response ? "server" : "internet";
    };

    const fetchListTransferArea = async () => {
      try {
        isLoadingData.value = true;
        const response = await ManageAreaController.getTransferArea(
          props.data.uid,
        );

        if (response.data) {
          const mapped = response.data.geoAddresses.map((item) => ({
            id: item.geoAddressId,
            villageName: item.villageName,
            subDistrictName: item.subDistrictName,
            districtName: item.districtName,
            provinceName: item.provinceName,
          }));

          originalTransferAreaData.value = mapped;

          pagination.value.allTotalData = originalTransferAreaData.value.length;
          pagination.value.totalData = originalTransferAreaData.value.length;
        }
      } catch (error) {
        console.error("error fetch list data:", error);
        setErrorCause(error);
      } finally {
        isLoadingData.value = false;
      }
    };

    const filteredTransferAreaData = computed(() => {
      const keyword = search.value.toLowerCase().trim();

      if (keyword.length < 3) {
        return originalTransferAreaData.value;
      }

      return originalTransferAreaData.value.filter((item: any) => {
        return (
          item.villageName?.toLowerCase().includes(keyword) ||
          item.subDistrictName?.toLowerCase().includes(keyword) ||
          item.districtName?.toLowerCase().includes(keyword) ||
          item.provinceName?.toLowerCase().includes(keyword)
        );
      });
    });

    const paginatedTransferAreaData = computed(() => {
      const start = (pagination.value.page - 1) * pagination.value.limit;

      const end = start + pagination.value.limit;

      return filteredTransferAreaData.value.slice(start, end);
    });

    // sorting
    const sortData = (data: any[], key: string, order: "asc" | "desc") => {
      return [...data].sort((a, b) => {
        const valA = a[key]?.toString().toLowerCase() ?? "";
        const valB = b[key]?.toString().toLowerCase() ?? "";

        if (valA < valB) return order === "asc" ? -1 : 1;
        if (valA > valB) return order === "asc" ? 1 : -1;
        return 0;
      });
    };

    const handleSort = (sort: { key: string; order: "asc" | "desc" }) => {
      sortKey.value = sort.key;
      sortOrder.value = sort.order;

      originalTransferAreaData.value = sortData(
        originalTransferAreaData.value,
        sortKey.value,
        sortOrder.value,
      );

      // reset pagination
      pagination.value.page = 1;
      pagination.value.totalData = pagination.value.allTotalData ?? 0;
    };

    const onPageChange = (page: number) => {
      pagination.value.page = page;

      const totalAll = pagination.value.allTotalData ?? 0;
      const used = (page - 1) * pagination.value.limit;

      pagination.value.totalData = Math.max(totalAll - used, 0);
    };

    const handleSelectAll = (value: any[]) => {
      selectedIds.value = value;
    };

    const onSelectRow = (value: any) => {
      selectedIds.value = value;
    };

    const isSelectedTransferArea = computed(() => selectedIds.value.length && destinationValue.value);

    watch(isShowModal, async (newVal) => {
        if (newVal) {
          await fetchListTransferArea();
        } else {
          // reset
          search.value = "";
          sortKey.value = null;
          sortOrder.value = null;
          selectedIds.value = [];
          selectSearchDataDestination.value = [];
          destinationValue.value = undefined;
        }
    },{ immediate: true });

    watch(filteredTransferAreaData, (val) => {
      pagination.value.allTotalData = val.length;
      pagination.value.page = 1;
      pagination.value.totalData = val.length;
    });

    return {
      selectSearchDataDestination,
      destinationValue,
      isShowModal,
      search,
      columns,
      sortKey,
      sortOrder,
      onSearch,
      handleConfirmation,
      onSelectSearchDestination,
      onCloseSelectSearchDestination,
      onChooseSelectSearchDestination,
      statusSelectSearchDestination,
      selectSearchDestinationNotFound,
      pagination,
      fetchPartnerAreaList,
      isLoadingFilter,
      isLoadingData,
      filteredTransferAreaData,
      paginatedTransferAreaData,
      handleSort,
      onPageChange,
      handleSelectAll,
      onSelectRow,
      startIndex,
      errorCause,
      fetchListTransferArea,
      isSelectedTransferArea,
    };
  },
});
